"use client";

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useSavedVacancies } from '@/context/SavedVacanciesContext';
import { useLanguage } from '@/context/LanguageContext';
import { useAlert } from '@/context/AlertContext';
import ApplyModal from './ApplyModal';

interface VacancyActionsProps {
  jobId: number | string;
  styles: any;
  companyName?: string;
  companyLogo?: string;
  applyLink?: string;
  applicationEmail?: string;
  whatsappNumber?: string;
  jobTitle?: string;
  isExpired?: boolean;
}

export default function VacancyActions({ jobId, styles, companyName, companyLogo, applyLink, applicationEmail, whatsappNumber, jobTitle, isExpired }: VacancyActionsProps) {
  const { saveVacancy, removeVacancy, isSaved, isSent, markAsSent, removeSentVacancy, sentVacancies } = useSavedVacancies();
  const { t, language } = useLanguage();
  const { showAlert } = useAlert();
  const [showSuccess, setShowSuccess] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  
  
  React.useEffect(() => {
    setIsClient(true);
    
    // Auto-verify if application was deleted by company
    if (isSent(jobId)) {
      let userEmail = '';
      if (typeof window !== 'undefined') {
        userEmail = localStorage.getItem('userEmail') || '';
        if (!userEmail) {
          const match = document.cookie.match(/(^| )userEmail=([^;]+)/);
          if (match) userEmail = decodeURIComponent(match[2]);
        }
      }
      
      if (userEmail) {
        import('@/app/actions/applicationActions').then(({ checkIfUserApplied }) => {
          checkIfUserApplied(jobId, userEmail).then(isStillActive => {
            if (!isStillActive) {
              removeSentVacancy(jobId);
            }
          }).catch(console.error);
        });
      }
    }
  }, [jobId, isSent, removeSentVacancy]);

  const handleSave = () => {
    if (isSaved(jobId)) {
      removeVacancy(jobId);
    } else {
      saveVacancy(jobId);
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
      }, 2000);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: document.title,
        url: window.location.href
      }).catch(console.error);
    } else {
      navigator.clipboard.writeText(window.location.href);
      showAlert(t('Link copied to clipboard!'), 'Success', 'success');
    }
  };

  const saved = isClient ? isSaved(jobId) : false;
  const applied = isClient ? isSent(jobId) : false;

  console.log('VacancyActions Debug SSR:', {
    jobId,
    jobIdType: typeof jobId,
    isSent: isSent(jobId),
    applied,
    sentVacanciesLength: sentVacancies.length
  });

  if (!isClient) {
    return (
      <div className={styles.actions} style={{ minHeight: '40px' }}>
      </div>
    );
  }

  return (
    <>
      <div className={styles.actions}>
        <button 
          className={`${styles.iconBtn} ${styles.saveBtn}`}
          onClick={handleSave}
          title={saved ? t("Unsave vacancy") : t("Save vacancy")}
          style={{ color: saved ? '#16a34a' : '#6b7280' }}
        >
          {saved ? (
             <svg width="18" height="18" viewBox="0 0 24 24" fill="#16a34a" stroke="#16a34a" strokeWidth="2"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path></svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path></svg>
          )}
          <span style={{ fontWeight: 600 }}>{saved ? t("Saved") : t("Save")}</span>
        </button>
        
        <button className={styles.circleBtn} onClick={handleShare} title={t("Share vacancy")}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2"><circle cx="18" cy="5" r="3"></circle><circle cx="6" cy="12" r="3"></circle><circle cx="18" cy="19" r="3"></circle><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line></svg>
        </button>

        {isExpired ? (
          <button 
            className={styles.sendBtn}
            disabled
            style={{
              backgroundColor: '#9ca3af',
              color: '#ffffff',
              cursor: 'not-allowed',
              opacity: 0.85
            }}
            title={t('This vacancy has expired')}
          >
            {t('Expired')}
          </button>
        ) : (
          <>
            {whatsappNumber && (
              <button 
                className={styles.sendBtn}
                onClick={() => {
                  const rawNum = whatsappNumber.replace(/\D/g, '');
                  const url = window.location.href;
                  const title = jobTitle || t('this position');
                  let message = '';
                  
                  if (language === 'en') {
                    message = `Hello!\n\nI am reaching out regarding the *${title}* position posted on MyJobs.\n\nLink: ${url}\n\nI would love to learn more about this opportunity and submit my application. Looking forward to hearing from you!`;
                  } else {
                    message = `Përshëndetje!\n\nPo ju shkruaj në lidhje me pozicionin e lirë të punës për *${title}*, të publikuar në MyJobs.\n\nLinku: ${url}\n\nJam shumë i/e interesuar për këtë mundësi dhe do të doja të aplikoja. Në pritje të një përgjigjeje nga ana juaj!`;
                  }
                  
                  window.open(`https://wa.me/${rawNum}?text=${encodeURIComponent(message)}`, '_blank');
                }}
                style={{
                  backgroundColor: '#25D366',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/></svg>
                {t('Apply with WhatsApp')}
              </button>
            )}

            {(applyLink || applicationEmail) && (
              <button 
                className={styles.sendBtn} 
                onClick={async () => {
                  if (isChecking) return;
                  if (applyLink) {
                    window.open(applyLink, '_blank');
                  } else {
                    if (applied) {
                      let userEmail = '';
                      if (typeof window !== 'undefined') {
                        userEmail = localStorage.getItem('userEmail') || '';
                        if (!userEmail) {
                          const match = document.cookie.match(/(^| )userEmail=([^;]+)/);
                          if (match) userEmail = decodeURIComponent(match[2]);
                        }
                      }
                      
                      if (userEmail) {
                        setIsChecking(true);
                        try {
                          const { checkIfUserApplied } = await import('@/app/actions/applicationActions');
                          const isStillActive = await checkIfUserApplied(jobId, userEmail);
                          if (isStillActive) {
                            showAlert(
                              language === 'sq' 
                                ? 'Ju keni tashmë një aplikim aktiv për këtë pozicion. Nëse dëshironi të riaplikoni, ju lutemi prisni derisa kompania të fshijë aplikimin tuaj të mëparshëm.'
                                : 'You already have an active application for this position. If you wish to re-apply, please wait until the company removes your previous application.', 
                              language === 'sq' ? 'Aplikim Ekzistues' : 'Existing Application', 
                              'info'
                            );
                            setIsChecking(false);
                            return;
                          }
                        } catch (e) {
                          console.error(e);
                        }
                        setIsChecking(false);
                      }
                    }
                    setShowApplyModal(true);
                  }
                }}
                style={{
                  backgroundColor: (!applyLink && applied) ? '#10b981' : undefined,
                  cursor: isChecking ? 'wait' : 'pointer',
                  opacity: (!applyLink && applied) || isChecking ? 0.9 : 1
                }}
              >
                {isChecking ? (language === 'sq' ? 'Po kontrollohet...' : 'Checking...') : (!applyLink && applied) ? t('Applied') : t('Apply')}
              </button>
            )}
          </>
        )}
      </div>

      {showSuccess && isClient && typeof document !== 'undefined' && createPortal(
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          pointerEvents: 'none'
        }}>
          <div style={{
            background: '#1f2937', // dark background matching screenshot
            color: 'white',
            padding: '1rem 1.5rem',
            borderRadius: '0.75rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2)',
            pointerEvents: 'auto'
          }}>
            <div style={{
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              background: '#34d399', // green tick background
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>
            <span style={{ fontWeight: 500, fontSize: '0.95rem' }}>Vacancy saved successfully</span>
            <button 
              onClick={() => setShowSuccess(false)}
              style={{ background: 'transparent', border: 'none', color: '#9ca3af', cursor: 'pointer', display: 'flex', alignItems: 'center', marginLeft: '0.5rem' }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            </button>
          </div>
        </div>,
        document.body
      )}


      {isClient && typeof document !== 'undefined' && createPortal(
        <ApplyModal 
          isOpen={showApplyModal} 
          onClose={() => setShowApplyModal(false)} 
          companyName={companyName}
          companyLogo={companyLogo}
          jobId={jobId}
          jobTitle={jobTitle}
          applicationEmail={applicationEmail}
        />,
        document.body
      )}
    </>
  );
}
